<template>
  <div
    class="bg-white group-hover:bg-gray-50 transition-colors duration-150 ease-in-out"
    v-bind="$attrs"
  >
    <div
      class="flex items-center justify-between px-6 pt-8"
      @click="open = !open"
    >
      <div class="flex items-center space-x-3">
        <div
          v-if="!disableDraggable && projectsStore.project?.unarchived"
          class="drag-handle cursor-grab active:cursor-grabbing p-1 rounded-md hover:bg-gray-200 transition-colors duration-200"
          @click.stop
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4 text-gray-400">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M4 8h16M4 16h16"
    />
</svg>
        </div>

        <button
          data-test="open-trade"
          type="button"
          class="p-1 rounded-md hover:bg-gray-200 "
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-600 transition-all duration-200"
            :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
        </button>

        <button
          v-if="projectsStore.project?.unarchived"
          type="button"
          class="p-1 rounded-md hover:bg-blue-100 transition-colors duration-200"
          @click.stop="$emit('edit')"
        >
          <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-blue-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
    />
</svg>
        </button>

        <button
          v-if="projectsStore.project?.unarchived"
          type="button"
          class="p-1 rounded-md hover:bg-red-100 transition-colors duration-200"
          @click.stop="$emit('delete')"
        >
          <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    stroke-width="1.5"
    stroke="currentColor"
 class="w-5 h-5 text-red-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
    />
</svg>
        </button>

        <div class="flex items-start space-x-2">
          <span class="font-semibold text-gray-900">{{ trade.billNumber }}</span>

          <span class="text-gray-700">{{ trade.name }}</span>
        </div>
      </div>

      <div class="flex space-x-3 items-center">
        <div class="text-right">
          <p class="text-sm font-medium text-gray-900">
            {{ australianCurrency(totalTradeCost) }}
          </p>
        </div>

        <button
          v-if="projectsStore.project?.unarchived"
          type="button"
          class="p-1 rounded-md hover:bg-blue-100 transition-colors duration-200"
          @click.stop="createTradeItem"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-blue-600">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>
        </button>
      </div>
    </div>

    <div
      v-if="open && tradeItems.length"
      class="border-t border-gray-200 bg-gray-50 mt-4"
    >
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider child:px-6 child:py-3">
            <th>
              {{ $t('HeadContract.IndirectBillOfQuantities.TradeRow.table.item') }}
            </th>

            <th>
              {{ $t('HeadContract.IndirectBillOfQuantities.TradeRow.table.unit') }}
            </th>

            <th class="text-right">
              {{ $t('HeadContract.IndirectBillOfQuantities.TradeRow.table.quantity') }}
            </th>

            <th class="text-right">
              {{ $t('HeadContract.IndirectBillOfQuantities.TradeRow.table.rate') }}
            </th>

            <th class="text-right">
              {{ $t('HeadContract.IndirectBillOfQuantities.TradeRow.table.amount') }}
            </th>
          </thead>

          <Sortable
            :list="tradeItems"
            item-key="id"
            tag="tbody"
            class="bg-white"
            :class="{ 'opacity-50 pointer-events-none': updateSortAction.is(States.LOADING) }"
            :disabled="!projectsStore.project?.unarchived || disableDraggable"
            handle=".drag-handle"
            @end="changeSortOrder"
          >
            <template
              v-if="updateSortAction.is(States.LOADING)"
              #header
            >
              <div class="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bottom-1/2">
                <AppSpinner />
              </div>
            </template>

            <template #item="{element}">
              <TradeRowItem
                :project-unarchived="projectsStore.project?.unarchived"
                :trade-item="element"
                @edit="editTradeItem(element)"
                @delete="deleteTradeItem(element)"
              />
            </template>
          </Sortable>
        </table>
      </div>
    </div>

    <div
      v-else-if="open"
      class="border-y border-gray-200 px-6 py-8 text-center mt-4"
    >
      <div class="mx-auto h-8 w-8 text-gray-300 mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-8 h-8">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
      </div>

      <h3 class="text-sm font-medium text-gray-900 mb-1">
        {{ $t('HeadContract.IndirectBillOfQuantities.TradeRow.noTradeItemsFound') }}
      </h3>

      <p class="text-sm text-gray-500">
        {{ $t('HeadContract.IndirectBillOfQuantities.TradeRow.addFirstTradeItem') }}
      </p>
    </div>
  </div>

  <slot :open="open" />
</template>

<script setup lang="ts">
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency } from '@/helpers';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useProjectsStore } from '@/store/projects';
import { useTradeItemsStore } from '@/store/tradeItems';
import { Sortable } from 'sortablejs-vue3';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import TradeItemSaveModal from './TradeItemSaveModal.vue';
import TradeRowItem from './TradeRowItem.vue';

const props = defineProps<{
  trade: Trade;
  disableDraggable: boolean;
}>();

const emits = defineEmits<{
  (e: 'edit');
  (e: 'delete');
  (e: 'modalOpen');
  (e: 'modalClose');
  (e: 'createTradeItem', data: { trade: Trade });
  (e: 'editTradeItem', data: { tradeItem: TradeItem; trade: Trade });
}>();

const open = ref(false);
const tradeItemsStore = useTradeItemsStore();
const projectsStore = useProjectsStore();

const tradeItems = computed(() => {
  return tradeItemsStore.models
    .with('package_trade_items', (query) => {
      query.with('package');
    })
    .where('trade_id', props.trade.id)
    .orderBy('sort_order')
    .get();
});

const totalTradeCost = computed(() => {
  return tradeItems.value.reduce((total, current) => {
    return total + current.budget;
  }, 0);
});

const show = ref(false);

watch(show, (newValue) => {
  if(newValue) {
    emits('modalOpen');
  } else {
    emits('modalClose');
  }
});

const editTradeItem = (tradeItem: TradeItem) => {
  emits('editTradeItem', { tradeItem, trade: props.trade });
};

const createTradeItem = () => {
  open.value = true;
  emits('createTradeItem', { trade: props.trade });
};

const deleteTradeItemAction = useStoreApiAction(tradeItemsStore.deleteTradeItem);

const deleteTradeItem = (tradeItem: TradeItem) => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Trade Item?',
    text: `You are about to delete ${tradeItem.name}. This action cannot be undone.`,
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      deleteTradeItemAction.request(tradeItem.id).then(() => {
        Swal.fire({
          icon: 'warning',
          title: 'Trade Item Deleted.',
        });
      });
    }
  });
};

const updateSortAction = useStoreApiAction(tradeItemsStore.updateSort);

const changeSortOrderForTradeItem = (tradeItem: TradeItem, index: number) => {
  updateSortAction.request(tradeItem.id, {
    sort_order: index,
  }).catch((error) => {
    Swal.fire({
      title: 'Something went wrong.',
      icon: 'error',
    });
  });
};

const changeSortOrder = (e) => {
  const item = tradeItems.value.at(e.oldIndex);

  changeSortOrderForTradeItem(item, e.newIndex + 1);
};

defineOptions({
  inheritAttrs: false,
});
</script>

<style scoped>
.drag-handle {
  cursor: grab;
}

.drag-handle:active {
  cursor: grabbing;
}

/* Ensure the draggable element doesn't interfere with the handle */
:deep(.sortable-ghost) .drag-handle {
  cursor: grabbing;
}

:deep(.sortable-chosen) .drag-handle {
  cursor: grabbing;
}
</style>
